import {
  HomeIcon,
  LanguagesIcon,
  SparklesIcon,
  CalendarCheckIcon,
  ReceiptTextIcon,
  UserCheckIcon,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { christmasHeroImage, decorationServices, lightingCategories } from './christmasLighting';

export type IconItem = { title: string; description: string; icon: LucideIcon };

export const christmasAbout = {
  eyebrow: 'About Us',
  title: 'Holiday lighting from the team that cares for your lawn.',
  paragraphs: [
    'Chacon’s Lawncare Christmas Decoration Services brings the same attention to detail we put into every lawn to your holiday display. We design and install Christmas lights and decorations for homes and businesses in the Augusta, Georgia area.',
    'Every project starts with a visit to measure your property, so your estimate is based on your home or business — not a one-size-fits-all price. We speak English and Spanish, so you can talk with us in the language you prefer.',
  ],
  image: lightingCategories[0].image,
  imageAlt: lightingCategories[0].alt,
};

export type ProcessStep = { title: string; description: string; image: string; alt: string };

export const processSteps: ProcessStep[] = [
  {
    title: 'Request Your Free Estimate',
    description:
      'Fill out our estimate form or give us a call. Tell us about your property and the Christmas decorations you’d like, and we’ll take it from there.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/c1e5d427-b5b7-4c30-8861-2dcd95036556.jpg',
    alt: 'Illustration of a homeowner calling Chacon’s Lawncare about Christmas lights, with the yellow Chacon’s Lawncare truck in the driveway',
  },
  {
    title: 'Property Measurement and Design',
    description:
      'We visit your property to measure rooflines, trees, bushes, and walkways, then plan a lighting layout around your preferences. You’re welcome to be present, but it isn’t required.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/ecf717dc-e1db-4154-89a2-8b59cbc387ca.jpg',
    alt: 'Illustration of a Chacon’s Lawncare technician measuring a home’s roofline and planning the light layout',
  },
  {
    title: 'Professional Installation',
    description:
      'Our team installs your lights and decorations safely and carefully, using the right ladders and equipment so every line looks clean and even.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/3d94f281-9299-4bbf-8b08-089d5341fb87.jpg',
    alt: 'Illustration of technicians safely installing Christmas lights on a roofline using a ladder',
  },
  {
    title: 'Enjoy Your Christmas Lights',
    description:
      'Sit back and enjoy a warm, festive home all season long — no tangled strands, no ladders, and no stress.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/efadd857-e2da-4f38-8180-c820826fa8bf.jpg',
    alt: 'Illustration of a home glowing at night with warm Christmas lights, wreaths, and lit trees',
  },
  {
    title: 'Removal and Storage',
    description:
      'After the holidays, we carefully take everything down and organize the lights and equipment, so your property is back to normal without the hassle.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/97b80660-3ccf-4c6d-bf30-90563bd291f1.jpg',
    alt: 'Illustration of technicians removing Christmas lights and packing them into storage bins in the yellow Chacon’s Lawncare truck',
  },
];


export const galleryImages = [
  { src: christmasHeroImage, alt: 'Large home decorated with roofline lights, wreaths, garlands, and lit trees' },
  ...decorationServices.map((s) => ({ src: s.image, alt: s.alt })),
  { src: lightingCategories[1].image, alt: lightingCategories[1].alt },
];

export const whyChooseItems: IconItem[] = [
  { title: 'Free Personalized Estimates', description: 'Every estimate is prepared after measuring your property.', icon: ReceiptTextIcon },
  { title: 'Custom Lighting Design', description: 'Displays planned around your home or building and your style.', icon: SparklesIcon },
  { title: 'Residential & Commercial', description: 'Lighting for homes, storefronts, offices, and restaurants.', icon: HomeIcon },
  { title: 'English & Spanish', description: 'Talk with Yoskar in English or Wilmer en español.', icon: LanguagesIcon },
  { title: 'Installation & Removal', description: 'We put it up for the season and take it down afterward.', icon: CalendarCheckIcon },
  { title: 'No Need to Be Home', description: 'We can measure without you, or coordinate a visit if you prefer.', icon: UserCheckIcon },
];

export const christmasFaqs = [
  {
    q: 'How does the free estimate work?',
    a: 'Submit the estimate form or call us. We visit your property, measure the areas you want decorated, and prepare a personalized estimate based on your preferences.',
  },
  {
    q: 'Do I need to be home during the property measurement?',
    a: 'No. You are welcome to be present, but it is not required. If you would like to be there, check the box on the form and we will contact you to coordinate an afternoon visit based on our availability.',
  },
  {
    q: 'What decorations do you install?',
    a: 'Roofline lights, wreaths, bush lights, tree lights, stake lights, and garlands — or a full Christmas decoration package.',
  },
  {
    q: 'Do you decorate commercial properties?',
    a: 'Yes. We provide Christmas lighting for both residential and commercial properties, including storefronts, offices, and restaurants.',
  },
  {
    q: 'Do you take the decorations down after the holidays?',
    a: 'Yes. Seasonal removal is part of our Christmas decoration service. Ask us about timing when you receive your estimate.',
  },
  {
    q: 'Can I see prices online?',
    a: 'Because every property is different, we don’t list prices online. Your estimate is prepared after we measure your property.',
  },
];
